/**
 * Suggests what a bank transaction is: a receipt from a customer, a payment to a vendor, a link to a
 * receipt/payment already entered by hand, or a non-party line (own transfer, bank charges, cash…).
 * Pure functions — no database access — so they can be tested on their own.
 *
 * Signals, strongest first:
 *   learned alias (user confirmed this narration name before) · name similarity · amount fit with the
 *   party's open bills · an existing receipt/payment of the same amount within ±3 days.
 */
import { parseNarration, normalizeName, paymentModeFor } from "./narration.js";
import { calculateFifoAllocations } from "../../invoices/utils/paymentCalculations.js";

const DAY = 86400000;

/** 0–100 similarity between a narration name and a party name (handles truncation like "RAVITEXTI"). */
export function nameScore(narrationName, partyName) {
  const a = normalizeName(narrationName);
  const b = normalizeName(partyName);
  if (!a || !b) return 0;
  const ca = a.replace(/\s+/g, "");
  const cb = b.replace(/\s+/g, "");
  if (ca === cb) return 100;
  // Banks cut long names: "RAVI TEXTI" for "RAVI TEXTILES", "SREERAM TR" for "SREE RAM TRADERS"
  if (ca.length >= 6 && (cb.startsWith(ca) || ca.startsWith(cb))) return 85;
  const ta = a.split(" ").filter((t) => t.length > 1);
  const tb = b.split(" ").filter((t) => t.length > 1);
  if (!ta.length || !tb.length) return 0;
  const hit = ta.filter((t) => tb.some((u) => u === t || (t.length >= 4 && (u.startsWith(t) || t.startsWith(u))))).length;
  const ratio = hit / Math.max(ta.length, Math.min(tb.length, 3));
  return Math.round(ratio * 80);
}

/** Keys an alias can be saved under for this narration (name, and bank·account-tail when present). */
export function aliasKeys(parsed) {
  const keys = [];
  if (parsed.key) keys.push(`N:${parsed.key}`);
  if (parsed.bank && parsed.accountTail) keys.push(`A:${parsed.bank}${parsed.accountTail}`);
  if (parsed.vpa) keys.push(`V:${parsed.vpa}`);
  return keys;
}

/** Party's open bills, oldest first; the bank account's own company's bills when it has any. */
export function openBillsFor(party, direction, ctx) {
  const list = direction === "in" ? ctx.invoices : ctx.purchases;
  const name = String(party.name || "").toLowerCase();
  const all = list
    .filter((b) => {
      const id = direction === "in" ? b.customerId : b.vendorId;
      const bName = String((direction === "in" ? b.customerName : b.vendorName) || "").toLowerCase();
      return (id === party.id || (name && bName === name)) && Number(b.balance || 0) > 0.009;
    })
    .map((b) => (direction === "in" ? b : {
      ...b,
      invoiceNo: b.vendorBillNo || b.id,
      invoiceDate: b.billDate || b.createdAt || ""
    }));
  const own = ctx.companyId ? all.filter((b) => b.companyId === ctx.companyId) : [];
  return own.length ? own : all;
}

function amountFit(rupees, bills) {
  const balances = bills.map((b) => Number(b.balance || 0));
  if (balances.some((v) => Math.abs(v - rupees) < 0.01)) return 20;
  const sorted = [...bills].sort((x, y) => String(x.invoiceDate || "").localeCompare(String(y.invoiceDate || "")));
  let sum = 0;
  for (const b of sorted) {
    sum += Number(b.balance || 0);
    if (Math.abs(sum - rupees) < 0.01) return 15;
    if (sum > rupees) break;
  }
  const due = balances.reduce((s, v) => s + v, 0);
  return due > 0 && rupees <= due + 0.01 ? 5 : 0;
}

function findExistingPayment(txn, party, direction, ctx, rupees) {
  const type = direction === "in" ? "PAYMENT_IN" : "PAYMENT_OUT";
  const when = new Date(`${txn.date}T00:00:00`).getTime();
  return ctx.payments.filter((p) => {
    if (p.type !== type || p.status === "VOID" || p.bankTxnId) return false;
    if (party && p.partyId !== party.id) return false;
    if (Math.abs(Number(p.amount || 0) - rupees) >= 0.01) return false;
    const pd = new Date(`${p.paymentDate}T00:00:00`).getTime();
    return Math.abs(pd - when) <= 3 * DAY;
  });
}

/**
 * Suggestion for one transaction:
 * { action: "create"|"link"|"category"|"none", confidence: "high"|"medium"|"none",
 *   direction, party, allocations, totalApplied, unallocatedAmount, payment, category, parsed, reasons }
 */
export function suggestMatch(txn, ctx) {
  const parsed = parseNarration(txn.description);
  const direction = txn.creditPaise ? "in" : "out";
  const rupees = (txn.creditPaise || txn.debitPaise) / 100;
  const base = { parsed, direction, rupees, paymentMode: paymentModeFor(parsed.mode), reference: parsed.ref };

  // Non-party lines
  if (parsed.mode === "BANK") return { ...base, action: "category", category: "charges", confidence: "high", reasons: ["Bank charges / interest / tax"] };
  if (parsed.mode === "ATM" || parsed.mode === "CASH") return { ...base, action: "category", category: "cash", confidence: "medium", reasons: ["Cash / ATM"] };
  const ownName = parsed.key && ctx.ownNames.some((n) => nameScore(parsed.name, n) >= 85);
  if (parsed.mode === "SELF" || ownName) return { ...base, action: "category", category: "ownTransfer", confidence: "high", reasons: ["Transfer between own accounts"] };

  // Candidate parties: learned alias first, then name similarity
  const wanted = direction === "in" ? ["customer", "both"] : ["vendor", "both"];
  let best = null;
  const aliasHit = aliasKeys(parsed).map((k) => ctx.aliases[k]).find(Boolean);
  if (aliasHit) {
    const party = ctx.parties.find((p) => p.id === aliasHit.partyId);
    if (party) best = { party, score: 100, reason: "Remembered from earlier" };
  }
  if (!best && parsed.key) {
    ctx.parties.forEach((party) => {
      const typeOk = wanted.includes(party.partyType || "customer");
      const score = nameScore(parsed.name, party.name) - (typeOk ? 0 : 15);
      if (score >= 45 && (!best || score > best.score)) best = { party, score, reason: "Name in narration" };
    });
  }

  if (!best) {
    // No party by name — an existing receipt/payment of exactly this amount is still a good hint
    const lone = findExistingPayment(txn, null, direction, ctx, rupees);
    if (lone.length === 1) {
      const party = ctx.parties.find((p) => p.id === lone[0].partyId) || { id: lone[0].partyId, name: lone[0].partyName };
      return { ...base, action: "link", confidence: "medium", party, payment: lone[0], reasons: ["Same amount recorded within 3 days"] };
    }
    return { ...base, action: "none", confidence: "none", reasons: [] };
  }

  return { ...base, ...suggestForParty(txn, best.party, ctx, { score: best.score, reason: best.reason }) };
}

/**
 * Suggestion once the party is known (also used when the user picks a party by hand):
 * link to a receipt/payment already recorded for this amount, else create one settling the oldest bills.
 */
export function suggestForParty(txn, party, ctx, { score = 100, reason = "Chosen by you", bills: chosenBills = null } = {}) {
  const parsed = parseNarration(txn.description);
  const direction = txn.creditPaise ? "in" : "out";
  const rupees = (txn.creditPaise || txn.debitPaise) / 100;
  const base = { parsed, direction, rupees, paymentMode: paymentModeFor(parsed.mode), reference: parsed.ref, party };
  const bills = openBillsFor(party, direction, ctx);
  const existing = chosenBills ? [] : findExistingPayment(txn, party, direction, ctx, rupees);
  if (existing.length) {
    return { ...base, action: "link", confidence: "high", payment: existing[0], bills, reasons: [reason, "Already recorded by hand"] };
  }
  const fit = amountFit(rupees, bills);
  // Amount equals one bill's balance → that's the bill being paid; otherwise settle oldest first
  const exact = chosenBills ? null : bills
    .filter((b) => Math.abs(Number(b.balance || 0) - rupees) < 0.01)
    .sort((x, y) => String(x.invoiceDate || "").localeCompare(String(y.invoiceDate || "")))[0];
  const fifo = calculateFifoAllocations(chosenBills || (exact ? [exact] : bills), rupees);
  const confident = score >= 100 || (score >= 80 && fit >= 15);
  return {
    ...base,
    action: "create",
    confidence: confident ? "high" : "medium",
    bills,
    allocations: fifo.allocations.filter((a) => a.allocatedAmount > 0),
    totalApplied: fifo.totalApplied,
    unallocatedAmount: fifo.unallocatedAmount,
    reasons: [reason, exact ? "Amount matches a bill" : fit >= 15 ? "Amount matches open bills" : fit ? "Within total due" : bills.length ? "More than total due (advance)" : "No open bills (advance)"]
  };
}
